// Чип с множествен избор за реда с филтрите на справката.
//
// Изглежда като останалите чипове на реда (36px, същите рамки и тонове), а
// попъпът вътре е СЪЩИЯТ чеклист като филтъра в колона на таблицата —
// търсачка над шест опции, „Всички"/„Изчисти", чекбокс + етикет + брояч.
// Стои в antd `Popover` нарочно: `SharedAccountGate` разпознава отворен antd
// слой по DOM и не пуска Esc към глобалната навигация „назад".

import { useEffect, useMemo, useRef, useState } from "react";
import { Checkbox, Input, Popover } from "antd";
import { CaretDownOutlined, CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { useViewport } from "../../../../hooks/useViewport";

export interface FilterChipOption {
  value: string;
  label: string;
}

/** Над този брой опции чеклистът получава собствена търсачка. */
const SEARCH_THRESHOLD = 6;

type Props = {
  /** Етикетът в покой („Всички станции") и коренът на активния („Станции"). */
  allLabel: string;
  label: string;
  options: FilterChipOption[];
  selected: string[];
  onChange: (values: string[]) => void;
};

export const FilterChip = ({
  allLabel,
  label,
  options,
  selected,
  onChange,
}: Props) => {
  const { t } = useTranslation();
  const { isNarrow } = useViewport();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Esc затваря ПОПЪПА и спира събитието — иначе същото натискане минава и
  // през глобалната навигация „назад".
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) => option.label.toLowerCase().includes(needle));
  }, [options, query]);

  const isActive = selected.length > 0;
  const selectedLabel =
    selected.length === 1
      ? (options.find((option) => option.value === selected[0])?.label ??
        selected[0])
      : String(selected.length);

  const toggle = (value: string) => {
    onChange(
      selected.includes(value)
        ? selected.filter((item) => item !== value)
        : [...selected, value],
    );
  };

  const content = (
    <div className="flex w-[236px] flex-col gap-2 py-0.5">
      <p className="px-0.5 font-pos-mono text-[10px] font-bold tracking-[0.1em] text-muted">
        {label.toUpperCase()}
      </p>
      {options.length > SEARCH_THRESHOLD ? (
        <Input
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t("dataTable.filterSearch")}
          allowClear
          // Под 16px iOS Safari зуумва страницата при фокус в поле.
          className={isNarrow ? "!text-[16px]" : "!text-[13px]"}
        />
      ) : null}

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => onChange(visible.map((option) => option.value))}
          className="text-[11.5px] font-bold text-brand-deep-ink hover:underline"
        >
          {t("dataTable.filterSelectAll")}
        </button>
        <button
          type="button"
          onClick={() => onChange([])}
          disabled={!isActive}
          className="text-[11.5px] font-bold text-muted hover:text-ink disabled:opacity-40"
        >
          {t("dataTable.filterClear")}
        </button>
      </div>

      <div className="-mx-1 max-h-[228px] overflow-y-auto px-1">
        {visible.length ? (
          <ul className="flex flex-col gap-0.5">
            {visible.map((option) => (
              <li key={option.value}>
                <label className="flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 hover:bg-shade-1">
                  <Checkbox
                    checked={selected.includes(option.value)}
                    onChange={() => toggle(option.value)}
                  />
                  <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium text-ink">
                    {option.label}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-1.5 py-3 text-center text-[12px] font-medium text-muted">
            {t("dataTable.filterNoValues")}
          </p>
        )}
      </div>
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomLeft"
      content={content}
      arrow={false}
    >
      <span className="group/chip relative inline-flex shrink-0">
        <button
          ref={triggerRef}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          className={`inline-flex h-9 items-center gap-1.5 rounded-lg border pl-3.5 text-[12.5px] font-bold transition-colors ${
            isActive ? "pr-7" : "pr-3"
          } ${
            isActive
              ? "border-brand-tint2 bg-brand-tint text-brand-deep-ink"
              : "border-shade-3 bg-surface text-soft hover:bg-fill"
          }`}
        >
          {isActive ? (
            <>
              <span>{label}</span>
              <span aria-hidden className="text-faint">·</span>
              <span className="max-w-[140px] truncate font-pos-mono text-[11.5px]">
                {selectedLabel}
              </span>
            </>
          ) : (
            <>
              <span>{allLabel}</span>
              <CaretDownOutlined className="text-[9px] text-muted" />
            </>
          )}
        </button>
        {isActive ? (
          // Бързото махане стои ВЪРХУ чипа, вдясно — без да отваря попъпа.
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onChange([]);
            }}
            aria-label={t("dataTable.filterClear")}
            className="absolute right-1.5 top-1/2 grid size-5 -translate-y-1/2 place-items-center rounded-md text-brand-deep-ink transition-colors hover:bg-brand-tint2"
          >
            <CloseOutlined className="text-[9px]" />
          </button>
        ) : null}
      </span>
    </Popover>
  );
};
